import { RadioGroup } from "@headlessui/react";
import { captureException, captureMessage, withScope } from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FCC } from "types";

import { Sim } from "@/assets/icons/ds";
import { AppearTransition } from "@/components/Interface/AppearTransition/AppearTransition";
import { Button } from "@/components/Interface/Button/Button";
import { RadioButtonOption } from "@/components/Interface/FormFields/RadioButton/RadioButton";
import { HighlightPanel } from "@/components/Interface/HighlightPanel/HighlightPanel";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import { StepFlowNavigation } from "@/components/Layout/BaseLayouts/StepFlowLayout/StepFlowNavigation";
import { TwoColumnLayout } from "@/components/Layout/BaseLayouts/TwoColumnLayout";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { PaymentMeanSection } from "@/components/Portal/DeviceManagement/Roaming/CheckoutRoamingOption/PaymentMeanSection/PaymentMeanSection";
import { getHeader } from "@/components/Portal/DeviceManagement/Roaming/roamingUtils";
import { SubscriptionInfoBox } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionInfoBox/SubscriptionInfoBox";
import { LightboxWrapper } from "@/components/Portal/Documents/PaymentMethods/LightboxWrapper/LightboxWrapper";
import { useGetCampaignEndDate } from "@/hooks/useGetCampaignEndDate";
import { useHandleCheckoutError } from "@/hooks/useHandleCheckoutError";
import { useHandleError } from "@/hooks/useHandleError";
import { transformPaymentMean } from "@/hooks/usePaymentMode";
import { useQueryParam } from "@/hooks/useQueryParam";
import { useSimIdFromQueryParam } from "@/hooks/useSimIdFromQueryParam";
import { FullScreenLoadingContext } from "@/provider/FullScreenLoadingProvider/FullScreenLoadingProvider";
import { portalRoutes } from "@/router/routes/portalRoutes";
import {
  useAddOptionToContract,
  useCreateTransaction,
  useGetAvailableSunriseRoamingPackages,
  useGetProduct,
  useGetSim,
  useOrderRoamingPackage,
  usePayTransaction,
  useUpdateSim,
} from "@/services/api";
import {
  Basket,
  BasketSubscriberOrderType,
  CreateTransactionDatatransOrderType,
  DatatransPaymentResponseStatus,
  GetAvailableSunriseRoamingPackagesPackageType,
} from "@/services/model";
import {
  portalTariffProductIDAtom,
  tariffCheckoutPaymentPersistenceAtom,
} from "@/utils/atoms";
import { getCreditAmount } from "@/utils/basketUtils";
import { ChargeTypeIDs, TOTAL_DAYS_IN_YEAR } from "@/utils/constants.ts";
import { getLocalizedDateString } from "@/utils/dateHelpers";
import {
  getFlatNameFromLocalFlatSpeed,
  getTariffType,
  hasDifferentSuccessor,
  TariffTypes,
} from "@/utils/deviceUtils";
import { env } from "@/utils/environmentHelpers";
import { get3DsBrowserInfo } from "@/utils/lightboxHelpers";
import { resolveAliasAfterTariffChange } from "@/utils/simAliasDefaults";
import {
  Duration,
  findDurationPeer,
  getNextPaymentDate,
  getSubscriptionRecurringDateOffsetDays,
  TransformedProductsById,
  transformProducts,
} from "@/utils/tariffUtils";
import { getCurrencyFromLookupValue } from "@/utils/translationHelpers";

import { SubscriptionDueNow } from "./SubscriptionDueNow/SubscriptionDueNow";

type FormProps = {
  paymentProvider: string;
};

export const getVoiceOptionEntryFromRateBasket = (
  rateBasket: Basket | undefined | null,
) => rateBasket?.entries?.[0]?.options?.[0];

export const SubscriptionCheckout: FCC = () => {
  const { watch, control } = useForm<FormProps>();
  const [isSubmittingCheckout, setIsSubmittingCheckout] = useState(false);
  const [paymentStatus] = useQueryParam("payment");
  const [simId] = useSimIdFromQueryParam();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const handleError = useHandleError();
  const { setFullScreenLoading } = useContext(FullScreenLoadingContext);
  const { mutateAsync: updateSimAlias } = useUpdateSim();

  const [selectedRateId, setPortalTariffProductID] = useAtom(
    portalTariffProductIDAtom,
  );
  const [tariffPaymentPersistence, setTariffPaymentPersistence] = useAtom(
    tariffCheckoutPaymentPersistenceAtom,
  );

  // Local active rate — can diverge from atom when billing period is switched.
  // Falls back to the persisted product id so the checkout stays functional after a page reload
  // (portalTariffProductIDAtom is not persisted, but tariffCheckoutPaymentPersistenceAtom is).
  const [activeRateId, setActiveRateId] = useState<number | undefined>(
    selectedRateId ?? tariffPaymentPersistence?.product?.id,
  );

  // Keep local state in sync when the atom changes (e.g. user navigates back and re-selects).
  useEffect(() => {
    if (selectedRateId !== undefined) {
      setActiveRateId(selectedRateId);
    }
  }, [selectedRateId]);

  const { data: simDetail, isSuccess: hasFetchedSim } = useGetSim(
    { simId: parseInt(simId) },
    {
      query: {
        enabled: !isNaN(parseInt(simId)),
        placeholderData: tariffPaymentPersistence?.simDetail,
        onSuccess: (sim) =>
          setTariffPaymentPersistence((prev) => ({ ...prev, simDetail: sim })),
      },
    },
  );

  const {
    data: subscriberOrderResponse,
    isSuccess: hasFetchedOrder,
    isPlaceholderData: isOrderPlaceholder,
  } = useOrderRoamingPackage(
    Number(simId),
    {
      // @ts-ignore
      optionId: activeRateId,
    },
    {
      query: {
        refetchOnWindowFocus: false,
        enabled: activeRateId !== undefined,
        onSuccess: (res) =>
          setTariffPaymentPersistence((prev) => ({
            ...prev,
            subscriberOrderResponse: res,
          })),
        onError: (error) => {
          handleError(error);
          const billingPeriodFlag = isBillingPeriodSwitch
            ? "&billingPeriodSwitch=true"
            : "";
          navigate(
            `${portalRoutes.manageDevice.subscription.change}?simId=${simId}${billingPeriodFlag}`,
            { replace: true },
          );
        },
        placeholderData: tariffPaymentPersistence?.subscriberOrderResponse,
      },
    },
  );

  const rateBasket = subscriberOrderResponse?.basket;
  const subscriberOrderType = rateBasket?.subscriberOrderType;
  const discountAmount = rateBasket?.total?.oneTimeCampaign?.amount;
  const credit = getCreditAmount(rateBasket?.balance);
  const totalOnceAmount =
    (rateBasket?.total?.oneTime?.amount || 0) - (discountAmount || 0) - credit;
  const creditTimeRemaining = rateBasket?.total?.oneTime?.amountDiscounts;
  const recurringAmount = rateBasket?.total?.recurring?.amount;

  const sumIsZero = totalOnceAmount <= 0;
  const { isInvoice, isCreditCard, maskedCardNumberString, cardType } =
    transformPaymentMean(subscriberOrderResponse?.paymentMean);

  const shouldHidePaymentSelection =
    subscriberOrderType === BasketSubscriberOrderType.DOWNGRADE &&
    (isInvoice || isCreditCard);

  const { data: dataProduct, isSuccess: hasFetchedProduct } = useGetProduct(
    // @ts-ignore
    activeRateId,
    {
      expanded: true,
      client: Number(env.clientNumericId ?? 1),
    },
    {
      query: {
        enabled:
          activeRateId !== undefined && !isNaN(Number(env.clientNumericId)),
        onSuccess: (product) =>
          setTariffPaymentPersistence((prev) => ({ ...prev, product })),
        placeholderData: tariffPaymentPersistence?.product,
      },
    },
  );

  // Billing period switch — fetch available packages to find monthly/yearly peers.
  const { data: availablePackagesData } = useGetAvailableSunriseRoamingPackages(
    parseInt(simId),
    { packageType: GetAvailableSunriseRoamingPackagesPackageType.Data_CH },
    {
      query: {
        enabled: !isNaN(parseInt(simId)) && activeRateId !== undefined,
        refetchOnWindowFocus: false,
      },
    },
  );

  const processedProductsById = useMemo((): TransformedProductsById => {
    if (!availablePackagesData?.subscriberPackageData) {
      return {};
    }

    return transformProducts(availablePackagesData.subscriberPackageData)
      .processedProductsById;
  }, [availablePackagesData]);

  const currentProduct =
    activeRateId !== undefined
      ? processedProductsById[String(activeRateId)]
      : undefined;

  const monthlyPeer = findDurationPeer(
    currentProduct,
    processedProductsById,
    Duration.month,
  );
  const yearlyPeer = findDurationPeer(
    currentProduct,
    processedProductsById,
    Duration.year,
  );
  const canShowBillingPeriodSwitch =
    !!monthlyPeer && !!yearlyPeer && activeRateId !== undefined;

  const currentDuration =
    currentProduct?.duration ??
    (currentProduct?.minContractDur === TOTAL_DAYS_IN_YEAR
      ? Duration.year
      : Duration.month);
  const selectedDuration =
    currentDuration === Duration.year ? Duration.year : Duration.month;

  const [isSwitchingBillingPeriod, setIsSwitchingBillingPeriod] =
    useState(false);
  const pendingBillingPeriodScroll = useRef(false);

  const [billingPeriodSwitchParam] = useQueryParam("billingPeriodSwitch");
  // True when the user arrived via the "Wechseln" shortcut in SubscriptionStatus
  // rather than through the normal rate-selection step.
  const isBillingPeriodSwitch = billingPeriodSwitchParam === "true";

  // Auto-select the opposite billing-period peer once the product catalogue
  // has loaded. The ref prevents the effect from re-firing after the switch
  // updates activeRateId (which would change selectedDuration and re-trigger).
  const hasAutoSwitchedBillingPeriod = useRef(false);
  useEffect(() => {
    if (!isBillingPeriodSwitch || hasAutoSwitchedBillingPeriod.current) {
      return;
    }
    const targetPeer =
      selectedDuration === Duration.month ? yearlyPeer : monthlyPeer;

    if (!targetPeer?.id) {
      return;
    }
    hasAutoSwitchedBillingPeriod.current = true;
    setIsSwitchingBillingPeriod(true);
    pendingBillingPeriodScroll.current = true;
    setActiveRateId(targetPeer.id);
  }, [isBillingPeriodSwitch, selectedDuration, yearlyPeer, monthlyPeer]);

  const scrollToCheckoutTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBillingPeriodChange = async (duration: Duration) => {
    if (duration === selectedDuration || isSwitchingBillingPeriod) {
      return;
    }
    const peer = duration === Duration.year ? yearlyPeer : monthlyPeer;

    if (!peer?.id) {
      return;
    }
    setIsSwitchingBillingPeriod(true);
    setActiveRateId(peer.id);
    scrollToCheckoutTop();
    // isSwitchingBillingPeriod is reset once the order query resolves with the new rate.
  };

  // Reset the switching flag only once the order query returns FRESH data for
  // the new rate. Placeholder data still represents the previous basket — clearing
  // the flag then would allow submitting the wrong basket ID.
  useEffect(() => {
    if (!isOrderPlaceholder) {
      if (pendingBillingPeriodScroll.current) {
        pendingBillingPeriodScroll.current = false;
        scrollToCheckoutTop();
      }
      setIsSwitchingBillingPeriod(false);
    }
  }, [subscriberOrderResponse, isOrderPlaceholder]);

  const tariffType = getTariffType(dataProduct?.id);

  const handleSuccess = async () => {
    setFullScreenLoading(false);
    setPortalTariffProductID();
    setTariffPaymentPersistence(undefined);

    const nextAlias = resolveAliasAfterTariffChange({
      currentAlias: simDetail?.alias,
      newProductId: activeRateId,
      language: i18n.language,
    });

    if (nextAlias && simDetail?.simId) {
      try {
        await updateSimAlias({
          params: { simId: simDetail.simId },
          data: { sim_alias: nextAlias },
        });
      } catch {
        // Tariff change already succeeded; alias sync is best-effort / silent.
      }
    }

    await Promise.all([
      queryClient.invalidateQueries(["/sim/sim"]),
      queryClient.invalidateQueries(["/sim/sims"]),
    ]);
    setIsSubmittingCheckout(false);
    navigate(
      `${portalRoutes.manageDevice.subscription.display}?simId=${simDetail?.simId}`,
      { replace: true },
    );
  };

  const isSuccess = paymentStatus === "success";
  useEffect(() => {
    if (isSuccess && rateBasket?.id && !isSubmittingCheckout) {
      setIsSubmittingCheckout(true);
      handleSuccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, handleSuccess]);

  const {
    mutate: createTransaction,
    isLoading: isCreatingTransaction,
    data: datatransRequestDocument,
  } = useCreateTransaction();

  const handleTransactionError = (err: Error, errMessage?: string) => {
    setFullScreenLoading(false);
    setIsSubmittingCheckout(false);
    handleError(err, errMessage);
    withScope(function (scope) {
      scope.setFingerprint(["/contract/serviceId/options"]);
      scope.setTag("section", "portal");
      scope.setTag("domain", "SubscriptionCheckout");
      scope.setTag("endpoint", "addOptionToContract");
      scope.setExtra("simId", simId);
      scope.setExtra("basket", rateBasket);
      scope.setContext("paymentContext", {
        isInvoice,
        isCreditCard,
        maskedCardNumberString,
        cardType,
      });
      captureException(err);
    });
  };

  const { mutate: payTransaction } = usePayTransaction({
    mutation: {
      onSuccess: (data) => {
        if (data.status === DatatransPaymentResponseStatus.OK) {
          handleSuccess();
        } else {
          handleTransactionError(
            new Error("Payment failure"),
            `${t("Error.payment.fail")} ${t("Error.payment.pleaseCheckData")}`,
          );
        }
      },
      onError: (err) => handleTransactionError(err),
    },
  });

  const {
    mutate: addOptionToContract,
    isLoading: isAddingOptionLoading,
    isSuccess: isAddingOptionDone,
  } = useAddOptionToContract({
    mutation: {
      onSuccess: () => handleSuccess(),
      onError: (err) => handleTransactionError(err),
    },
  });

  const minContractDurationDays =
    subscriberOrderResponse?.minContractDuration || 30;
  const recurringDateDays = getSubscriptionRecurringDateOffsetDays({
    subscriberOrderType,
    remainingDays: simDetail?.remainingDays,
    minContractDurationDays,
  });
  const recurringDate =
    recurringDateDays !== undefined
      ? t("Onboarding.sections.checkout.summary.recurringDate", {
          date: getLocalizedDateString(
            getNextPaymentDate(recurringDateDays),
            i18n.language,
          ),
        })
      : undefined;
  const billingPeriod = t(
    "Onboarding.sections.checkout.summary.billingPeriod",
    { days: minContractDurationDays },
  );
  const recurringBillingPeriod = t(
    "portal:cockpit.managementTile.tariff.payInterval",
    { days: minContractDurationDays },
  );
  const recurringCaption = t(
    "portal:cockpit.managementTile.watchSim.checkout.today",
  );
  const isVoiceOption = dataProduct?.displayValue?.includes("Voice Option");
  const hasWatchSim = !!simDetail?.watchSim;

  const watchedPaymentProvider = watch("paymentProvider");

  useHandleCheckoutError(sumIsZero);

  const canSkipPaymentWithoutPaymentMean = isInvoice || isCreditCard;

  const handleSubmit = () => {
    const basketId = rateBasket?.id;

    if (basketId === undefined) {
      captureMessage("Wanted to submit undefined basket id!");
      handleError("Wanted to submit undefined basket id!");

      return;
    }
    if (totalOnceAmount === undefined) {
      captureMessage("Wanted to charge undefined oneTime amount");
      handleError("Wanted to charge undefined oneTime amount");

      return;
    }
    if (!isSubmittingCheckout) {
      setFullScreenLoading(true, t("Onboarding.sections.checkout.loading"));
      if (canSkipPaymentWithoutPaymentMean) {
        setIsSubmittingCheckout(true);
        if (isCreditCard && totalOnceAmount > 0) {
          payTransaction({
            params: {
              simId: parseInt(simId),
              basketId,
              datatransOrderType:
                CreateTransactionDatatransOrderType.AddOptionToContract,
            },
          });
        } else {
          addOptionToContract({
            serviceId: Number(simId),
            data: {
              header: getHeader(),
              basket: rateBasket,
              basketId,
            },
          });
        }
      } else {
        createTransaction({
          params: {
            simId: parseInt(simId),
            basketId,
            datatransOrderType:
              CreateTransactionDatatransOrderType.AddOptionToContract,
            ...get3DsBrowserInfo(),
          },
        });
      }
    }
  };

  const voiceOptionEntry = getVoiceOptionEntryFromRateBasket(rateBasket);

  const voiceOptionOneTimeCharge = useMemo(
    () =>
      voiceOptionEntry?.oneTimeCharges?.filter(
        (value) => value.chargeType?.id === ChargeTypeIDs.VoiceOption,
      ),
    [voiceOptionEntry?.oneTimeCharges],
  );
  const dataProductOneTimeCharge = useMemo(
    () =>
      voiceOptionEntry?.oneTimeCharges?.filter(
        (value) => value.chargeType?.id === 4,
      ),
    [voiceOptionEntry?.oneTimeCharges],
  );
  const voiceOptionCharge = useMemo(
    () =>
      dataProduct?.charges?.filter(
        (charge) => charge.id === voiceOptionOneTimeCharge?.[0]?.charge?.id,
      ),
    [dataProduct, voiceOptionOneTimeCharge],
  );

  const { campaignEndDate } = useGetCampaignEndDate(rateBasket);
  const currency = getCurrencyFromLookupValue(
    rateBasket?.currency || { id: 1 },
  );

  const watchSimPrice = useMemo(
    () =>
      voiceOptionEntry?.options?.[0]?.oneTimeCharges?.find(
        (charge) => charge.chargeType?.id === 4,
      )?.amount,
    [voiceOptionEntry?.options],
  );

  const discountName =
    voiceOptionEntry?.discounts?.[0]?.discountName ??
    t("Onboarding.sections.checkout.summary.discount");

  const disableSuccessButton =
    isCreatingTransaction ||
    isAddingOptionLoading ||
    isAddingOptionDone ||
    isSwitchingBillingPeriod ||
    isOrderPlaceholder ||
    (activeRateId === undefined &&
      tariffPaymentPersistence?.product?.id === undefined) ||
    (!watchedPaymentProvider && !(isInvoice || isCreditCard));

  const hasFetchedEverything =
    hasFetchedSim && hasFetchedOrder && hasFetchedProduct;

  const aboveColumns = (
    <div className="flex flex-col gap-4">
      <p>
        <Trans t={t} values={{ count: simDetail?.remainingDays ?? 0 }}>
          {subscriberOrderType === BasketSubscriberOrderType.DOWNGRADE
            ? "cockpit.managementTile.tariff.checkout.tariffActivation.asSuccessor"
            : "cockpit.managementTile.tariff.checkout.tariffActivation.now"}
        </Trans>
      </p>

      {hasDifferentSuccessor(simDetail) && (
        <p data-testid="eliminiating-followup-info">
          <Trans
            i18nKey="cockpit.managementTile.tariff.checkout.eliminatingFollowup"
            values={{
              description: getFlatNameFromLocalFlatSpeed(
                simDetail?.successorForRenew?.description,
              ),
            }}
          />
        </p>
      )}

      {!isVoiceOption &&
        hasWatchSim &&
        tariffType === TariffTypes.dataTariff && (
          <HighlightPanel
            headline={t(
              "cockpit.managementTile.tariff.checkout.watchSimWarning",
            )}
          >
            <Trans components={[<span key="0" className="font-semibold" />]}>
              cockpit.managementTile.tariff.checkout.watchSimDeactivationHint
            </Trans>
          </HighlightPanel>
        )}
    </div>
  );

  const summaryLeft = (
    <ProfileCard
      variant="white"
      bodyGap={6}
      title={t("Onboarding.sections.checkout.summary.title")}
    >
      {simDetail?.alias && (
        <SubscriptionInfoBox className="flex flex-col gap-1">
          <p className="text-body-xs text-foreground-secondary">
            {t("Onboarding.sections.checkout.summary.assignedDevice")}
          </p>
          <p className="text-body-base font-semibold leading-none text-foreground-primary">
            {simDetail.alias}
          </p>
        </SubscriptionInfoBox>
      )}

      <div className="flex w-full flex-col gap-3">
        <p className="text-body-base font-semibold leading-none tracking-[0.32px] text-foreground-primary">
          {t("cockpit.managementTile.tariff.yourTariff")}
        </p>

        <SubscriptionInfoBox className="flex items-center gap-3">
          <Sim className="size-6 shrink-0 text-foreground-primary" />
          <p className="text-body-lg font-semibold leading-md text-foreground-primary">
            {dataProduct?.baseDisplayValue}
            {isVoiceOption &&
              " | " +
                t(
                  "cockpit.managementTile.details.sections.autoRenewal.summary.voiceOption",
                )}
            {hasWatchSim &&
              " | " +
                t(
                  "portal:cockpit.managementTile.details.sections.autoRenewal.summary.watchSimOption",
                )}
          </p>
        </SubscriptionInfoBox>
      </div>
    </ProfileCard>
  );

  const dueNowRight =
    rateBasket?.currency !== undefined && totalOnceAmount !== undefined ? (
      <SubscriptionDueNow
        totalOnceAmount={totalOnceAmount}
        discountOnceAmount={discountAmount}
        discountName={discountName}
        discountEndDate={campaignEndDate}
        credit={credit}
        creditTimeRemaining={creditTimeRemaining}
        currency={currency}
        billingPeriod={billingPeriod}
        dataRateName={dataProduct?.baseDisplayValue}
        dataRatePrice={dataProductOneTimeCharge?.[0]?.amount}
        voiceOptionPrice={voiceOptionCharge?.[0]?.amount}
        watchSimName={t("common.device.attributes.watchSim")}
        watchSimPrice={watchSimPrice}
        recurringAmount={recurringAmount}
        recurringDate={recurringDate}
        recurringBillingPeriod={recurringBillingPeriod}
        recurringCaption={recurringCaption}
      />
    ) : null;

  const paymentLeft =
    !shouldHidePaymentSelection && rateBasket !== undefined ? (
      <div className="flex w-full flex-col gap-4">
        <PaymentMeanSection
          isInvoice={isInvoice}
          isCreditCard={isCreditCard}
          cardType={cardType}
          maskedCardNumberString={maskedCardNumberString}
          control={control}
          enableShowCurrent={true}
          sumIsZero={sumIsZero}
        />
        <LightboxWrapper
          paymentProvider={watchedPaymentProvider}
          transactionId={datatransRequestDocument?.refNo}
          sign={datatransRequestDocument?.sign}
          enabled={datatransRequestDocument !== undefined}
        />
      </div>
    ) : null;

  const billingPeriodRight =
    !isBillingPeriodSwitch && canShowBillingPeriodSwitch ? (
      <ProfileCard
        dataTestid="checkout-billing-period-switch"
        variant="white"
        title={t("Onboarding.sections.checkout.billingPeriod.title")}
        subtitle={
          <Trans
            t={t}
            i18nKey="Onboarding.sections.checkout.billingPeriod.description"
            components={{ strong: <strong /> }}
          />
        }
      >
        <RadioGroup
          className="flex flex-col gap-2"
          value={selectedDuration}
          onChange={(duration: Duration) =>
            void handleBillingPeriodChange(duration)
          }
        >
          <RadioButtonOption
            name="subscription-billing-period"
            value={Duration.month}
            label={t("Onboarding.sections.checkout.billingPeriod.monthly")}
            disabled={isSwitchingBillingPeriod}
          />
          <div className="flex items-center gap-2">
            <RadioButtonOption
              name="subscription-billing-period"
              value={Duration.year}
              label={t("Onboarding.sections.checkout.billingPeriod.yearly")}
              disabled={isSwitchingBillingPeriod}
            />
            <span className="inline-flex h-6 shrink-0 items-center whitespace-nowrap rounded-full bg-surface-accent-rose px-2 py-1 text-body-xs font-semibold leading-none tracking-[0.24px] text-foreground-primary">
              {t("Onboarding.sections.checkout.billingPeriod.freeMonthBadge")}
            </span>
          </div>
        </RadioGroup>
      </ProfileCard>
    ) : undefined;

  return (
    <div
      className="flex w-full flex-col gap-6"
      id="checkout-tariff"
      data-testid={subscriberOrderType}
    >
      {!hasFetchedEverything ||
      isCreatingTransaction ||
      isAddingOptionLoading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      ) : (
        <AppearTransition>
          {!activeRateId && !tariffPaymentPersistence?.product?.id ? (
            <p data-testid="no-product-id">
              {t("cockpit.managementTile.tariff.checkout.noProductId")}
            </p>
          ) : (
            <div className="flex flex-col gap-6" id="checkout-layout">
              {aboveColumns}
              <TwoColumnLayout
                independentColumns
                rows={[
                  { left: summaryLeft, right: dueNowRight },
                  { left: paymentLeft, right: billingPeriodRight },
                ]}
              />
            </div>
          )}
        </AppearTransition>
      )}

      <StepFlowNavigation
        primaryButton={
          <Button
            dataTestid="success-button"
            variant="primary"
            disabled={disableSuccessButton}
            onClick={handleSubmit}
          >
            {t("common.buttons.submit")}
          </Button>
        }
        secondaryButton={
          <Button
            variant="secondary"
            onClick={() => {
              setTariffPaymentPersistence(undefined);
              navigate(
                isBillingPeriodSwitch
                  ? `${portalRoutes.manageDevice.subscription.display}?simId=${simId}`
                  : `${portalRoutes.manageDevice.subscription.change}?simId=${simId}`,
                { replace: true },
              );
            }}
          >
            {t("common.buttons.back")}
          </Button>
        }
      />
    </div>
  );
};
